import { useState, useEffect } from 'react'
import { apiError } from '../../lib/errors'
import { api as axiosApi, auditApi, supportApi, emailTemplatesApi, type AuditEntry, type SupportTicket, type EmailTemplate } from '../../lib/api'
import { TenantQuotaPanel, QuotaDefaultsPanel } from './AIQuotaPanels'
import { ParcelsMapPanel } from './ParcelsMapPanel'
import DocsPage from '../DocsPage'

// Shared form-control styling for admin modals. A bare <input>/<select>/<textarea>
// (no explicit background/color) falls back to the browser-default white background
// with grey inherited text — unreadable on the modal card, especially in light mode.
// This mirrors the create-tenant modal's local `inp`/`sel`; use it for every modal
// field so the slug/plan/note controls are legible in both light and dark mode.
const ADMIN_FIELD: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box',
  background: 'var(--color-background-tertiary)',
  border: '0.5px solid var(--color-border-secondary)',
  borderRadius: 8, color: 'var(--color-text-primary)',
  fontSize: 13, padding: '8px 10px', outline: 'none',
}
const ADMIN_SELECT: React.CSSProperties = { ...ADMIN_FIELD, cursor: 'pointer' }

// Per-tenant AI detection testing controls (grafty-api §176/§179). Reads/writes
// three flags via the generic admin flag-override API:
//   • ai_deepforest_detection — the default engine (Gemini vs external DeepForest)
//   • ai_engine_picker        — show a Gemini⟷DeepForest toggle in the farm-map UI
//   • ai_model_picker         — show a Gemini model dropdown in the farm-map UI
// A PUT sets the override on; DELETE clears it back to the default.
function DetectionEngineToggle({ tenantId, showToast }: { tenantId: string; showToast: (m: string) => void }) {
  const [engine, setEngine] = useState<'gemini' | 'deepforest'>('gemini')
  const [enginePicker, setEnginePicker] = useState(false)
  const [modelPicker, setModelPicker] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let alive = true
    setLoading(true)
    axiosApi.get(`/admin/flags/${tenantId}`)
      .then(r => {
        if (!alive) return
        const f = r.data?.resolved ?? {}
        setEngine(f.ai_deepforest_detection ? 'deepforest' : 'gemini')
        setEnginePicker(!!f.ai_engine_picker)
        setModelPicker(!!f.ai_model_picker)
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [tenantId])

  async function change(next: 'gemini' | 'deepforest') {
    const prev = engine
    setEngine(next); setSaving(true)
    try {
      if (next === 'deepforest') {
        await axiosApi.put(`/admin/flags/${tenantId}/ai_deepforest_detection`, { enabled: true, reason: 'enable DeepForest detection engine' })
      } else {
        await axiosApi.delete(`/admin/flags/${tenantId}/ai_deepforest_detection`) // clear override → default (Gemini)
      }
      showToast(next === 'deepforest' ? 'Tree detection → DeepForest' : 'Tree detection → Gemini (default)')
    } catch (e) {
      setEngine(prev)
      showToast(apiError(e, 'Failed to update engine'))
    } finally { setSaving(false) }
  }

  async function toggleFlag(flag: 'ai_engine_picker' | 'ai_model_picker', on: boolean) {
    const set = flag === 'ai_engine_picker' ? setEnginePicker : setModelPicker
    set(on); setSaving(true)
    try {
      if (on) await axiosApi.put(`/admin/flags/${tenantId}/${flag}`, { enabled: true, reason: 'enable in-UX detection picker' })
      else await axiosApi.delete(`/admin/flags/${tenantId}/${flag}`)
      showToast(`${flag === 'ai_engine_picker' ? 'Engine' : 'Model'} picker ${on ? 'on' : 'off'}`)
    } catch (e) {
      set(!on)
      showToast(apiError(e, 'Failed to update picker'))
    } finally { setSaving(false) }
  }

  const checkbox = (flag: 'ai_engine_picker' | 'ai_model_picker', checked: boolean, label: string) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} disabled={loading || saving} onChange={e => toggleFlag(flag, e.target.checked)} />
      {label}
    </label>
  )

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
        AI detection (testing)
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Default engine</label>
        <select
          value={engine}
          disabled={loading || saving}
          onChange={e => change(e.target.value as 'gemini' | 'deepforest')}
          style={{ fontSize: 12, border: '0.5px solid var(--color-border-secondary)', borderRadius: 6, padding: '4px 8px', background: 'var(--color-background-primary)', color: 'var(--color-text-primary)', cursor: 'pointer' }}
        >
          <option value="gemini">Gemini — in-house (default)</option>
          <option value="deepforest">DeepForest — external detector</option>
        </select>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
        {checkbox('ai_engine_picker', enginePicker, 'Engine picker in the farm-map UI')}
        {checkbox('ai_model_picker', modelPicker, 'Gemini model picker in the farm-map UI')}
      </div>
      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 6, lineHeight: 1.5 }}>
        Default engine = which detector runs when the tenant has no in-UX choice. The pickers let
        this tenant A/B engines / Gemini models from the farm map itself (for testing). DeepForest is a
        purpose-built tree-crown detector (scale-to-zero Cloud Run); if its service isn't deployed in
        this environment it silently falls back to Gemini. The tenant also needs AI + the tree-detection
        toggle on for any detection.
      </div>
    </div>
  )
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface PlatformMetrics {
  total_tenants: number
  active_tenants: number
  pending_approvals: number
  seedling_tenants: number
  grower_tenants: number
  estate_tenants: number
  mrr_total_usd: number
  new_tenants_this_month: number
  total_plants: number
  // CRO / revenue KPIs
  effective_mrr_usd: number
  arr_usd: number
  paying_tenants: number
  arpa_usd: number
  active_trials: number
  trial_tenants: number
  past_due_tenants: number
  mrr_at_risk_usd: number
  churn_rate_monthly_pct: number
  net_new_tenants: number
  mrr_growth_mom_pct: number
  ltv_estimate_usd: number
  churned_this_month: number
}

interface Application {
  id: string
  farm_name: string
  owner_name: string
  owner_email: string
  farm_location: string
  farm_size_acres?: number
  primary_growable: string
  requested_plan: string
  use_cases: string[]
  notes: string
  status: 'pending' | 'approved' | 'rejected'
  submitted_at: string
}

interface BetaSignupRow {
  id: string
  email: string
  name: string
  farm_name: string
  postal_code: string
  country: string
  status: string
  tenant_id?: string
  created_at: string
}

interface Tenant {
  id: string
  slug: string
  name: string
  plan: string
  db_tier: string
  status: string
  plant_count: number
  owner_email: string
  mrr_usd: number
  billing_status: string
  created_at: string
}

interface TenantMember {
  id: string
  email: string
  display_name: string
  role: string
  status: string
  last_login_at?: string
  created_at: string
}

interface FeatureUsageStat {
  feature: string
  event_count: number
  last_used_at: string
}

interface TenantDetail extends Tenant {
  members: TenantMember[]
  member_count: number
  audit_events_30d: number
  feature_usage: FeatureUsageStat[]
  harvest_count: number
  care_event_count: number
  expense_count: number
  storefront_charge_mode?: string
}

// ── Admin Portal ──────────────────────────────────────────────────────────────

// ── Audit helpers ─────────────────────────────────────────────────────────────

const ACTION_COLORS: Record<string, string> = {
  create: '#4CAF50', update: '#60A5FA', delete: '#F87171',
  login: '#A78BFA', logout: '#6B7280', export: '#F5A623',
}
function auditActionColor(action: string) {
  const verb = action.split('.')[0].toLowerCase()
  return ACTION_COLORS[verb] ?? '#888'
}
function relTime(iso: string) {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
}

// ── Admin audit log panel (shown inside the admin portal) ─────────────────────

function AdminAuditPanel({ tenants }: { tenants: { id: string; name: string; slug: string }[] }) {
  const [selectedTenantId, setSelectedTenantId] = useState('')
  const [since, setSince] = useState('')
  const [until, setUntil] = useState('')
  const [resourceType, setResourceType] = useState('')
  const [action, setAction] = useState('')
  const [entries, setEntries] = useState<AuditEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nextCursor, setNextCursor] = useState<string | undefined>()
  const [cursor, setCursor] = useState<string | undefined>()

  async function load(cur?: string) {
    if (!selectedTenantId) { setError('Select a tenant first'); return }
    setLoading(true); setError(null)
    try {
      const params = {
        ...(since && { since: new Date(since + 'T00:00:00').toISOString() }),
        ...(until && { until: new Date(until + 'T23:59:59').toISOString() }),
        ...(resourceType && { resource_type: resourceType }),
        ...(action && { action }),
        ...(cur && { cursor: cur }),
        limit: 50,
      }
      const res = await auditApi.listTenantLogs(selectedTenantId, params)
      setEntries(res.data.entries)
      setNextCursor(res.data.next_cursor)
      setCursor(cur)
    } catch (e) {
      setError(apiError(e, 'Failed to load audit logs'))
    } finally { setLoading(false) }
  }

  const RESOURCE_TYPES = ['plant','harvest','variety','vendor','care_event','irrigation_zone','inventory_item','propagation_batch','user','settings']
  const ACTIONS = ['create','update','delete','login','logout','export']

  const selS: React.CSSProperties = {
    background: 'var(--color-background-secondary)', border: '0.5px solid var(--color-border-secondary)',
    borderRadius: 6, padding: '5px 8px', color: 'var(--color-text-primary)', fontSize: 12, cursor: 'pointer',
  }

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 500, margin: '0 0 4px' }}>Tenant Audit Logs</h2>
      <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '0 0 20px' }}>
        Browse every change made inside any tenant — who did what, when, and what changed.
      </p>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <select style={{ ...selS, minWidth: 200 }} value={selectedTenantId} onChange={e => setSelectedTenantId(e.target.value)}>
          <option value="">— Select tenant —</option>
          {tenants.map(t => <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>)}
        </select>
        <input style={selS} type="date" value={since} onChange={e => setSince(e.target.value)} title="From date" />
        <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>→</span>
        <input style={selS} type="date" value={until} onChange={e => setUntil(e.target.value)} title="To date" />
        <select style={selS} value={resourceType} onChange={e => setResourceType(e.target.value)}>
          <option value="">All types</option>
          {RESOURCE_TYPES.map(rt => <option key={rt} value={rt}>{rt.replace(/_/g, ' ')}</option>)}
        </select>
        <select style={selS} value={action} onChange={e => setAction(e.target.value)}>
          <option value="">All actions</option>
          {ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <button
          onClick={() => load()}
          disabled={loading || !selectedTenantId}
          style={{ padding: '6px 16px', borderRadius: 8, background: '#2D6A2F', color: '#fff', border: 'none', fontSize: 12, cursor: loading || !selectedTenantId ? 'not-allowed' : 'pointer', opacity: !selectedTenantId ? 0.5 : 1 }}
        >
          {loading ? 'Loading…' : 'Load logs'}
        </button>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', background: '#FCEBEB', border: '0.5px solid #A32D2D', borderRadius: 8, fontSize: 13, color: '#A32D2D', marginBottom: 14 }}>
          {error}
        </div>
      )}

      {/* Entries table */}
      {entries.length > 0 && (
        <>
          <div style={{
            background: 'var(--color-background-primary)',
            border: '0.5px solid var(--color-border-tertiary)',
            borderRadius: 12, overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 140px 120px 90px', padding: '8px 14px', background: 'var(--color-background-secondary)', borderBottom: '0.5px solid var(--color-border-tertiary)' }}>
              {['Action', 'Resource', 'Actor', 'Time', 'IP'].map(h => (
                <span key={h} style={{ fontSize: 10, fontWeight: 600, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{h}</span>
              ))}
            </div>
            {entries.map((e, i) => {
              const color = auditActionColor(e.action)
              return (
                <div key={e.id} style={{ display: 'grid', gridTemplateColumns: '100px 1fr 140px 120px 90px', padding: '9px 14px', borderTop: i > 0 ? '0.5px solid var(--color-border-tertiary)' : 'none', alignItems: 'center' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 99, background: color + '1A', color, border: `1px solid ${color}33`, display: 'inline-block', textAlign: 'center' }}>
                    {e.action.split('.')[0]}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                      {e.resource_type.replace(/_/g, ' ')}
                    </span>
                    {e.resource_label && (
                      <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginLeft: 6 }}>— {e.resource_label}</span>
                    )}
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {e.actor_name ?? e.actor_email ?? 'system'}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                    {new Date(e.occurred_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    <span style={{ display: 'block', fontSize: 10, color: 'var(--color-text-tertiary)' }}>{relTime(e.occurred_at)}</span>
                  </span>
                  <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)', fontFamily: 'monospace' }}>
                    {e.ip_address ?? '—'}
                  </span>
                </div>
              )
            })}
          </div>
          {/* Pagination */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
            {cursor ? <button onClick={() => load(undefined)} style={selS}>← Newer</button> : <span />}
            {nextCursor ? <button onClick={() => load(nextCursor)} style={selS}>Older →</button> : <span />}
          </div>
        </>
      )}

      {!loading && entries.length === 0 && selectedTenantId && (
        <div style={{
          background: 'var(--color-background-primary)',
          border: '0.5px solid var(--color-border-tertiary)',
          borderRadius: 12, padding: '40px 0', textAlign: 'center',
          color: 'var(--color-text-tertiary)', fontSize: 13,
        }}>
          No audit entries found. Click "Load logs" to fetch, or adjust filters.
        </div>
      )}
    </div>
  )
}

// ── Main portal ───────────────────────────────────────────────────────────────

const BLANK_CREATE_TENANT = {
  slug: '', name: '', plan: 'seedling', owner_email: '', owner_name: '', farm_type: 'general',
}

const BLANK_RELEASE = { version: '', title: '', body_markdown: '', summary_bullets: ['', '', ''], is_published: false }

interface Release {
  id: string
  version: string
  title: string
  body_markdown: string
  summary_bullets: string[]
  is_published: boolean
  published_at?: string | null
}

// ── Growable reference CRUD (global species catalog) ─────────────────────────
interface GrowableRef {
  id: string
  common_name: string; scientific_name: string; latin_name: string
  family: string; category: string; description: string; flavor_profile: string
  culinary_uses: string[]; benefits: string[]; cautions: string[]
  pet_safety_dogs: string; pet_safety_cats: string; pet_safety_note: string
  ripe_months: number[]; fertilize_months: number[]; prune_months: number[]; spray_months: number[]
  propagation_methods: string[]; tags: string[]; is_verified: boolean
}
const PET_SAFETY = ['unknown', 'safe in small amounts', 'caution — large quantities', 'toxic — keep away']
const emptyGrowable = (): GrowableRef => ({
  id: '', common_name: '', scientific_name: '', latin_name: '', family: '', category: '',
  description: '', flavor_profile: '', culinary_uses: [], benefits: [], cautions: [],
  pet_safety_dogs: 'unknown', pet_safety_cats: 'unknown', pet_safety_note: '',
  ripe_months: [], fertilize_months: [], prune_months: [], spray_months: [],
  propagation_methods: [], tags: [], is_verified: true,
})
const csv = (a: string[]) => (a || []).join(', ')
const parseCsv = (s: string) => s.split(',').map(x => x.trim()).filter(Boolean)
const parseMonths = (s: string) => parseCsv(s).map(Number).filter(n => n >= 1 && n <= 12)

interface ProposedCrop { id: string; common_name: string; category: string; tenant_id: string; farm_name: string; created_at: string }
function ProposedCropsPanel({ showToast }: { showToast: (m: string, t?: 'success' | 'error') => void }) {
  const [rows, setRows] = useState<ProposedCrop[]>([])
  function load() { axiosApi.get('/admin/growable-ref/proposed').then(r => setRows(r.data.proposed || [])).catch(() => {}) }
  useEffect(load, [])
  async function approve(id: string) {
    try { await axiosApi.post(`/admin/growable-ref/${id}/approve`); showToast('Promoted to the global catalog'); load() }
    catch (e) { showToast(apiError(e, 'Approve failed'), 'error') }
  }
  async function reject(r: ProposedCrop) {
    if (!window.confirm(`Reject "${r.common_name}"? It's removed from the catalog (the tenant keeps their variety).`)) return
    try { await axiosApi.delete(`/admin/growable-ref/${r.id}`); showToast('Rejected'); load() }
    catch (e) { showToast(apiError(e, 'Reject failed'), 'error') }
  }
  if (!rows.length) return null
  return (
    <div style={{
      marginBottom: 20, borderRadius: 12, padding: 16,
      border: '1px solid color-mix(in srgb, var(--color-primary) 40%, transparent)',
      background: 'color-mix(in srgb, var(--color-primary) 8%, transparent)',
    }}>
      <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 4px' }}>Proposed crops ({rows.length})</h3>
      <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
        Custom crops growers added at signup/onboarding. Approve to add to the global catalog for everyone, or reject.
      </p>
      {rows.map(r => (
        <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '8px 0', borderTop: '1px solid var(--color-border)' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{r.common_name}{r.category ? ` · ${r.category}` : ''}</div>
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>{r.farm_name || 'a tenant'} · {r.created_at?.slice(0, 10)}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button onClick={() => approve(r.id)} style={{ background: 'var(--color-primary)', color: '#0E1209', border: 'none', borderRadius: 8, padding: '6px 12px', fontWeight: 600, cursor: 'pointer', fontSize: 12 }}>Approve</button>
            <button onClick={() => reject(r)} style={{ background: 'transparent', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 12 }}>Reject</button>
          </div>
        </div>
      ))}
    </div>
  )
}

// Email Templates console — override Loops transactional template IDs at runtime
// (test a new ID in dev, promote to prod later; no redeploy) + send a sample test.
function EmailTemplatesPanel({ showToast }: { showToast: (m: string, t?: 'success' | 'error') => void }) {
  const [rows, setRows] = useState<EmailTemplate[]>([])
  const [configured, setConfigured] = useState(true)
  const [loading, setLoading] = useState(true)
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [busyKey, setBusyKey] = useState('')
  const [testEmail, setTestEmail] = useState('')

  const load = () => {
    setLoading(true)
    emailTemplatesApi.list()
      .then(r => { setRows(r.data.templates); setConfigured(r.data.configured); setDrafts({}) })
      .catch(e => showToast(apiError(e, 'Failed to load templates'), 'error'))
      .finally(() => setLoading(false))
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [])

  const save = (key: string, value: string) => {
    setBusyKey(key)
    emailTemplatesApi.set(key, value.trim())
      .then(r => { setRows(r.data.templates); setDrafts(d => { const n = { ...d }; delete n[key]; return n }); showToast('Saved', 'success') })
      .catch(e => showToast(apiError(e, 'Save failed'), 'error'))
      .finally(() => setBusyKey(''))
  }
  const sendTest = (key: string) => {
    setBusyKey(key + ':test')
    emailTemplatesApi.test(key, testEmail.trim() || undefined)
      .then(r => showToast(r.data.configured ? `Test sent to ${r.data.to}` : `Email not configured — logged only (would send to ${r.data.to})`, 'success'))
      .catch(e => showToast(apiError(e, 'Test failed'), 'error'))
      .finally(() => setBusyKey(''))
  }

  const badge = (src: string) => {
    const map: Record<string, [string, string]> = {
      override: ['override', 'var(--color-primary)'],
      env: ['env default', 'var(--color-accent)'],
      unset: ['not set', '#F87171'],
    }
    const [label, color] = map[src] || [src, 'var(--color-text-muted)']
    return <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color, border: `1px solid ${color}`, borderRadius: 5, padding: '1px 6px' }}>{label}</span>
  }

  if (loading) return <div style={{ color: 'var(--color-text-secondary)', padding: 20 }}>Loading…</div>

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 4px', color: 'var(--color-text-primary)' }}>Email templates (Loops.so)</h3>
        <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.5 }}>
          Each transactional email resolves its Loops template ID: an override here wins over the <code>LOOPS_TMPL_*</code> env default. Changes apply immediately — no redeploy. Dev and prod are separate environments, so set an ID here, verify with a test send, then set the same ID in the other environment's console later.
        </p>
      </div>

      {!configured && (
        <div style={{ background: 'color-mix(in srgb, #F87171 12%, transparent)', border: '1px solid #F87171', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--color-text-primary)', marginBottom: 12 }}>
          <b>LOOPS_API_KEY is not set in this environment</b> — template sends are logged, not delivered. Set the secret to send real email.
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <label style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Send tests to:</label>
        <input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="(your email)" style={{ ...ADMIN_FIELD, width: 240 }} />
      </div>

      <div style={{ display: 'grid', gap: 8 }}>
        {rows.map(t => {
          const draft = drafts[t.key] ?? t.override_id
          const dirty = draft !== t.override_id
          return (
            <div key={t.key} style={{ border: '0.5px solid var(--color-border-secondary)', borderRadius: 10, padding: '10px 12px', background: 'var(--color-background-secondary)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--color-text-primary)' }}>{t.label}</span>
                {badge(t.source)}
                <code style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{t.key}</code>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <input
                  value={draft}
                  onChange={e => setDrafts(d => ({ ...d, [t.key]: e.target.value }))}
                  placeholder={t.env_id ? `env default: ${t.env_id}` : 'Loops template ID…'}
                  style={{ ...ADMIN_FIELD, flex: 1, minWidth: 200, fontFamily: 'monospace', fontSize: 12 }}
                />
                <button onClick={() => save(t.key, draft)} disabled={!dirty || busyKey === t.key}
                  style={{ background: dirty ? 'var(--color-primary)' : 'var(--color-background-tertiary)', color: dirty ? '#fff' : 'var(--color-text-tertiary)', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, cursor: dirty ? 'pointer' : 'default' }}>
                  {busyKey === t.key ? '…' : 'Save'}
                </button>
                {t.override_id && (
                  <button onClick={() => save(t.key, '')} disabled={busyKey === t.key}
                    style={{ background: 'none', border: '0.5px solid var(--color-border-secondary)', borderRadius: 8, padding: '7px 12px', fontSize: 12, cursor: 'pointer', color: 'var(--color-text-secondary)' }}>
                    Clear
                  </button>
                )}
                <button onClick={() => sendTest(t.key)} disabled={t.source === 'unset' || busyKey === t.key + ':test'}
                  style={{ background: 'none', border: '0.5px solid var(--color-border-secondary)', borderRadius: 8, padding: '7px 12px', fontSize: 12, cursor: t.source === 'unset' ? 'default' : 'pointer', color: t.source === 'unset' ? 'var(--color-text-tertiary)' : 'var(--color-text-secondary)', opacity: t.source === 'unset' ? 0.5 : 1 }}>
                  {busyKey === t.key + ':test' ? '…' : 'Send test'}
                </button>
              </div>
              {t.vars.length > 0 && (
                <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 5 }}>
                  vars: {t.vars.join(', ')}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function GrowableRefPanel({ showToast }: { showToast: (m: string, t?: 'success' | 'error') => void }) {
  const [refs, setRefs] = useState<GrowableRef[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<GrowableRef | null>(null)
  const [q, setQ] = useState('')

  function load() {
    setLoading(true)
    axiosApi.get('/admin/growable-ref').then(r => setRefs(r.data.refs || [])).catch(() => {}).finally(() => setLoading(false))
  }
  useEffect(load, [])

  async function save(g: GrowableRef) {
    try {
      if (g.id) await axiosApi.put(`/admin/growable-ref/${g.id}`, g)
      else await axiosApi.post('/admin/growable-ref', g)
      showToast(g.id ? 'Species updated' : 'Species added')
      setEditing(null); load()
    } catch (e) { showToast(apiError(e, 'Save failed'), 'error') }
  }
  async function del(g: GrowableRef) {
    if (!window.confirm(`Delete "${g.common_name}" from the catalog? This affects all tenants.`)) return
    try { await axiosApi.delete(`/admin/growable-ref/${g.id}`); showToast('Species deleted'); load() }
    catch (e) { showToast(apiError(e, 'Delete failed'), 'error') }
  }

  const filtered = refs.filter(r =>
    !q || r.common_name.toLowerCase().includes(q.toLowerCase()) || r.scientific_name.toLowerCase().includes(q.toLowerCase()))

  return (
    <div>
      <ProposedCropsPanel showToast={showToast} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <h2 style={{ fontSize: 20, fontWeight: 500, margin: 0 }}>Growable reference data</h2>
        <button onClick={() => setEditing(emptyGrowable())} style={{
          background: 'var(--color-primary)', color: '#0E1209', border: 'none', borderRadius: 8,
          padding: '8px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 13,
        }}>+ Add species</button>
      </div>
      <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 16 }}>
        Global species profiles that auto-populate new plant entries. Changes affect all tenants.
      </p>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search species…" style={{
        width: '100%', maxWidth: 320, marginBottom: 16, padding: '8px 12px', borderRadius: 8,
        border: '0.5px solid var(--color-border-tertiary)', background: 'var(--color-background-secondary)',
        color: 'var(--color-text-primary)', fontSize: 13,
      }} />
      {loading ? <p style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>Loading…</p> : (
        <div style={{ border: '0.5px solid var(--color-border-tertiary)', borderRadius: 12, overflow: 'hidden' }}>
          {filtered.length === 0 && <div style={{ padding: 20, color: 'var(--color-text-secondary)', fontSize: 13 }}>No species. Click “+ Add species”.</div>}
          {filtered.map(g => (
            <div key={g.id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
              borderBottom: '0.5px solid var(--color-border-tertiary)', fontSize: 13,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
                  {g.common_name}{' '}
                  {!g.is_verified && <span style={{ fontSize: 11, color: '#F5A623' }}>· unverified</span>}
                </div>
                <div style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>{g.scientific_name}{g.category ? ` · ${g.category}` : ''}</div>
              </div>
              <button onClick={() => setEditing(g)} style={ghostBtn}>Edit</button>
              <button onClick={() => del(g)} style={{ ...ghostBtn, color: '#E0533D' }}>Delete</button>
            </div>
          ))}
        </div>
      )}
      {editing && <GrowableEditModal initial={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  )
}
const ghostBtn: React.CSSProperties = {
  background: 'transparent', border: '0.5px solid var(--color-border-tertiary)', borderRadius: 6,
  padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: 'var(--color-text-primary)',
}

function GrowableEditModal({ initial, onClose, onSave }: {
  initial: GrowableRef; onClose: () => void; onSave: (g: GrowableRef) => void
}) {
  const [f, setF] = useState<GrowableRef>(initial)
  const set = <K extends keyof GrowableRef>(k: K, v: GrowableRef[K]) => setF(p => ({ ...p, [k]: v }))
  const fld: React.CSSProperties = {
    width: '100%', padding: '8px 10px', borderRadius: 8, fontSize: 13,
    border: '0.5px solid var(--color-border-tertiary)', background: 'var(--color-background-secondary)',
    color: 'var(--color-text-primary)', boxSizing: 'border-box',
  }
  const lbl: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }
  const row = (label: string, node: React.ReactNode) => (
    <div style={{ marginBottom: 12 }}><label style={lbl}>{label}</label>{node}</div>
  )
  const text = (k: keyof GrowableRef, ph = '') => (
    <input value={f[k] as string} onChange={e => set(k, e.target.value as never)} placeholder={ph} style={fld} />
  )
  const arr = (k: keyof GrowableRef, ph = '') => (
    <input value={csv(f[k] as string[])} onChange={e => set(k, parseCsv(e.target.value) as never)} placeholder={ph} style={fld} />
  )
  const months = (k: keyof GrowableRef) => (
    <input value={csv((f[k] as number[]).map(String))} onChange={e => set(k, parseMonths(e.target.value) as never)} placeholder="e.g. 6, 7, 8" style={fld} />
  )
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: 24,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--color-background-primary)', border: '0.5px solid var(--color-border-tertiary)',
        borderRadius: 16, padding: 24, width: 560, maxWidth: '100%',
      }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 18 }}>{f.id ? 'Edit species' : 'Add species'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {row('Common name *', text('common_name', 'Mango'))}
          {row('Scientific name *', text('scientific_name', 'Mangifera indica'))}
          {row('Other / latin name', text('latin_name'))}
          {row('Family', text('family', 'Anacardiaceae'))}
          {row('Category', text('category', 'mango'))}
          {row('Flavor profile', text('flavor_profile'))}
        </div>
        {row('Description', <textarea value={f.description} onChange={e => set('description', e.target.value)} rows={2} style={fld} />)}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {row('Ripe months', months('ripe_months'))}
          {row('Fertilize months', months('fertilize_months'))}
          {row('Prune months', months('prune_months'))}
          {row('Spray months', months('spray_months'))}
        </div>
        {row('Culinary uses (comma-sep)', arr('culinary_uses'))}
        {row('Benefits (comma-sep)', arr('benefits'))}
        {row('Cautions (comma-sep)', arr('cautions'))}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {row('Pet safety — dogs', (
            <select value={f.pet_safety_dogs} onChange={e => set('pet_safety_dogs', e.target.value)} style={fld}>
              {PET_SAFETY.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          ))}
          {row('Pet safety — cats', (
            <select value={f.pet_safety_cats} onChange={e => set('pet_safety_cats', e.target.value)} style={fld}>
              {PET_SAFETY.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          ))}
        </div>
        {row('Pet safety note', text('pet_safety_note'))}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {row('Propagation methods (comma-sep)', arr('propagation_methods'))}
          {row('Tags (comma-sep)', arr('tags'))}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, margin: '4px 0 16px', cursor: 'pointer' }}>
          <input type="checkbox" checked={f.is_verified} onChange={e => set('is_verified', e.target.checked)} />
          Verified (show in autocomplete + crop catalog)
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={ghostBtn}>Cancel</button>
          <button
            onClick={() => f.common_name.trim() && f.scientific_name.trim() && onSave(f)}
            disabled={!f.common_name.trim() || !f.scientific_name.trim()}
            style={{
              background: 'var(--color-primary)', color: '#0E1209', border: 'none', borderRadius: 8,
              padding: '8px 18px', fontWeight: 600, cursor: 'pointer', fontSize: 13,
              opacity: (!f.common_name.trim() || !f.scientific_name.trim()) ? 0.5 : 1,
            }}>Save</button>
        </div>
      </div>
    </div>
  )
}

export default function AdminPortal() {
  const [tab, setTab] = useState<'overview' | 'applications' | 'beta' | 'tenants' | 'support' | 'audit' | 'growable' | 'releases' | 'docs' | 'ai_quotas' | 'maps' | 'email'>('overview')
  const [metrics, setMetrics] = useState<PlatformMetrics | null>(null)
  const [applications, setApplications] = useState<Application[]>([])
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [selectedApp, setSelectedApp] = useState<Application | null>(null)
  const [approveModal, setApproveModal] = useState(false)
  const [approveForm, setApproveForm] = useState({ slug: '', plan: 'seedling', note: '' })
  const [betaSignups, setBetaSignups] = useState<BetaSignupRow[]>([])
  const [betaApproveModal, setBetaApproveModal] = useState(false)
  const [selectedBeta, setSelectedBeta] = useState<BetaSignupRow | null>(null)
  const [betaApproveForm, setBetaApproveForm] = useState({ slug: '', note: '' })
  const [createTenantModal, setCreateTenantModal] = useState(false)
  const [createTenantForm, setCreateTenantForm] = useState({ ...BLANK_CREATE_TENANT })
  const [releaseModal, setReleaseModal] = useState(false)
  const [releaseForm, setReleaseForm] = useState({ ...BLANK_RELEASE })
  const [releases, setReleases] = useState<Release[]>([])
  const [loading, setLoading] = useState(false)
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null)
  const [tenantDrawer, setTenantDrawer] = useState<TenantDetail | null>(null)
  const [drawerLoading, setDrawerLoading] = useState(false)

  const adminApi = {
    get: (path: string) => axiosApi.get(`/admin${path}`).then(r => r.data),
    post: (path: string, body?: unknown) => axiosApi.post(`/admin${path}`, body).then(r => r.data),
    put: (path: string, body?: unknown) => axiosApi.put(`/admin${path}`, body).then(r => r.data),
  }

  useEffect(() => {
    adminApi.get('/metrics').then(setMetrics).catch(() => {})
    adminApi.get('/applications?status=pending').then(d => setApplications(d.applications || [])).catch(() => {})
    adminApi.get('/beta-signups?status=pending').then(d => setBetaSignups(d.signups || [])).catch(() => {})
    adminApi.get('/tenants').then(d => setTenants(d.tenants || d || [])).catch(() => {})
    adminApi.get('/releases').then(d => setReleases(d.releases || [])).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function showToast(msg: string, type: 'success' | 'error' = 'success') {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3500)
  }

  async function handleApprove() {
    if (!selectedApp) return
    setLoading(true)
    try {
      await adminApi.post(`/applications/${selectedApp.id}/approve`, approveForm)
      setApplications(a => a.filter(x => x.id !== selectedApp.id))
      setApproveModal(false)
      setSelectedApp(null)
      showToast(`${selectedApp.farm_name} approved`)
      if (metrics) setMetrics({ ...metrics, pending_approvals: metrics.pending_approvals - 1 })
    } catch {
      showToast('Approval failed', 'error')
    }
    setLoading(false)
  }

  async function handleReject(app: Application, note: string) {
    await adminApi.post(`/applications/${app.id}/reject`, { note })
    setApplications(a => a.filter(x => x.id !== app.id))
    showToast(`${app.farm_name} rejected`)
  }

  async function handleBetaApprove() {
    if (!selectedBeta) return
    setLoading(true)
    try {
      const res = await adminApi.post(`/beta-signups/${selectedBeta.id}/approve`, betaApproveForm)
      setBetaSignups(b => b.filter(x => x.id !== selectedBeta.id))
      setBetaApproveModal(false)
      setSelectedBeta(null)
      showToast(`${selectedBeta.farm_name || selectedBeta.email} approved → ${res?.slug || 'tenant created'}`)
      // Refresh tenant list so the new beta tenant appears.
      adminApi.get('/tenants').then(d => setTenants(d.tenants || d || [])).catch(() => {})
    } catch (e) {
      showToast(apiError(e, 'Approval failed'), 'error')
    }
    setLoading(false)
  }

  async function handleBetaReject(row: BetaSignupRow) {
    const note = window.prompt(`Reject the beta signup from ${row.email}? (optional note)`) ?? null
    if (note === null) return // cancelled
    try {
      await adminApi.post(`/beta-signups/${row.id}/reject`, { note })
      setBetaSignups(b => b.filter(x => x.id !== row.id))
      showToast(`${row.email} rejected`)
    } catch {
      showToast('Reject failed', 'error')
    }
  }

  // Slugify a farm name for the approve modal's editable slug suggestion.
  function betaSlug(name: string) {
    return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 32)
  }

  async function handleCreateTenant() {
    if (!createTenantForm.slug || !createTenantForm.name || !createTenantForm.owner_email) return
    setLoading(true)
    try {
      const result = await adminApi.post('/tenants', createTenantForm)
      // Refetch the canonical list rather than optimistically prepending the
      // create response (whose shape can differ from list rows and caused a
      // 404 when the detail drawer was opened with a mismatched id).
      const fresh = await adminApi.get('/tenants')
      setTenants(fresh.tenants || fresh || [])
      setCreateTenantModal(false)
      setCreateTenantForm({ ...BLANK_CREATE_TENANT })
      const ownerEmail = result.owner_email || createTenantForm.owner_email
      if (result.invite_status === 'sent') {
        showToast(`Tenant "${result.name}" created — owner invite emailed to ${ownerEmail}`)
      } else {
        showToast(`Tenant "${result.name}" created, but the owner invite did not send (${result.invite_status || 'unknown'}). They can use "Forgot password" to set a password.`, 'error')
      }
      if (metrics) setMetrics({ ...metrics, total_tenants: metrics.total_tenants + 1, active_tenants: metrics.active_tenants + 1 })
    } catch (e) {
      showToast(apiError(e, 'Create failed'), 'error')
    }
    setLoading(false)
  }

  async function handleChangeTier(tenantId: string, newPlan: string) {
    await adminApi.put(`/tenants/${tenantId}/tier`, { plan: newPlan })
    setTenants(t => t.map(x => x.id === tenantId ? { ...x, plan: newPlan } : x))
    showToast('Plan updated')
  }

  async function handleChargeMode(tenantId: string, mode: string) {
    await adminApi.put(`/tenants/${tenantId}/charge-mode`, { mode })
    setTenantDrawer(t => (t && t.id === tenantId ? { ...t, storefront_charge_mode: mode } : t))
    showToast(`Storefront charges set to ${mode}`)
  }

  async function handleSuspend(tenantId: string) {
    if (!confirm('Suspend this tenant? They will lose access immediately.')) return
    await adminApi.post(`/tenants/${tenantId}/suspend`)
    setTenants(t => t.map(x => x.id === tenantId ? { ...x, status: 'suspended' } : x))
    showToast('Tenant suspended')
  }

  async function openTenantDrawer(tenantId: string) {
    setDrawerLoading(true)
    setTenantDrawer(null)
    try {
      const detail = await adminApi.get(`/tenants/${tenantId}`)
      setTenantDrawer(detail)
    } catch {
      showToast('Failed to load tenant detail', 'error')
    } finally {
      setDrawerLoading(false)
    }
  }

  async function handleDeleteTenant(tenantId: string, tenantName: string) {
    if (!confirm(`Permanently delete "${tenantName}"? This cannot be undone — all tenant data will be destroyed.`)) return
    try {
      await axiosApi.delete(`/admin/tenants/${tenantId}`)
      setTenants(t => t.filter(x => x.id !== tenantId))
      showToast(`Tenant "${tenantName}" deleted`)
      if (metrics) setMetrics({ ...metrics, total_tenants: Math.max(0, metrics.total_tenants - 1), active_tenants: Math.max(0, metrics.active_tenants - 1) })
    } catch (e) {
      showToast(apiError(e, 'Delete failed'), 'error')
    }
  }

  const planColor = (plan: string) =>
    ({ free: 'var(--color-text-tertiary)', starter: '#378ADD', pro: '#639922' }[plan] || 'var(--color-text-secondary)')

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      active: '#27500A', suspended: '#A32D2D', pending: '#633806', cancelled: '#A32D2D'
    }
    const bg: Record<string, string> = {
      active: '#EAF3DE', suspended: '#FCEBEB', pending: '#FAEEDA', cancelled: '#FCEBEB'
    }
    return (
      <span style={{
        background: bg[status] || '#F1EFE8',
        color: map[status] || '#444441',
        fontSize: 11, fontWeight: 500, padding: '2px 8px',
        borderRadius: 99, textTransform: 'capitalize',
      }}>
        {status}
      </span>
    )
  }

  return (
    <div style={{ minHeight: '100dvh', background: 'var(--color-background-tertiary)' }}>

      {/* Top nav */}
      <div style={{
        background: 'var(--color-background-primary)',
        borderBottom: '0.5px solid var(--color-border-tertiary)',
        padding: '0 24px', display: 'flex', alignItems: 'center', gap: 0, height: 56,
      }}>
        <span style={{ fontSize: 15, fontWeight: 500, marginRight: 32 }}>
          Grafty <span style={{ color: '#993C1D', fontSize: 11, fontWeight: 500, background: '#FAECE7', padding: '2px 6px', borderRadius: 4, marginLeft: 6 }}>superuser</span>
        </span>
        {(['overview', 'applications', 'beta', 'tenants', 'support', 'audit', 'growable', 'releases', 'docs', 'ai_quotas', 'maps', 'email'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 13, padding: '0 16px', height: 56,
              borderBottom: tab === t ? '2px solid var(--color-text-primary)' : '2px solid transparent',
              color: tab === t ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
              textTransform: 'capitalize',
            }}
          >
            {t === 'applications' && applications.length > 0
              ? `${t} (${applications.length})`
              : t === 'beta'
              ? (betaSignups.length > 0 ? `beta (${betaSignups.length})` : 'beta')
              : t === 'ai_quotas' ? 'AI Quotas' : t === 'email' ? 'Email' : t}
          </button>
        ))}
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 24px' }}>

        {/* ── OVERVIEW ─────────────────────────────────────────────────────── */}
        {tab === 'overview' && metrics && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 500, margin: '0 0 20px' }}>Platform overview</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 28 }}>
              {[
                { label: 'Total tenants', value: metrics.total_tenants },
                { label: 'Active', value: metrics.active_tenants },
                { label: 'Pending approvals', value: metrics.pending_approvals, warn: metrics.pending_approvals > 0 },
                { label: 'MRR', value: `$${metrics.mrr_total_usd.toFixed(0)}`, sub: '/mo' },
              ].map(({ label, value, warn, sub }) => (
                <div key={label} style={{
                  background: 'var(--color-background-primary)',
                  border: `0.5px solid ${warn ? 'var(--color-border-warning)' : 'var(--color-border-tertiary)'}`,
                  borderRadius: 12, padding: '16px 20px',
                }}>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 28, fontWeight: 500, color: warn ? 'var(--color-text-warning)' : 'var(--color-text-primary)' }}>
                    {value}{sub && <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--color-text-tertiary)', marginLeft: 2 }}>{sub}</span>}
                  </div>
                </div>
              ))}
            </div>

            {/* Revenue & growth — CRO KPIs */}
            <div style={{
              background: 'var(--color-background-primary)',
              border: '0.5px solid var(--color-border-tertiary)',
              borderRadius: 12, padding: '18px 22px', marginBottom: 28,
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>Revenue &amp; growth</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>CRO KPIs · effective (post-discount) revenue</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(148px, 1fr))', gap: 14 }}>
                {(() => {
                  const money = (n: number) => (n || 0) >= 1000 ? `$${((n || 0) / 1000).toFixed(1)}k` : `$${(n || 0).toFixed(0)}`
                  const cards: { label: string; value: string; sub?: string; badge?: string; badgeGood?: boolean; warn?: boolean }[] = [
                    { label: 'Effective MRR', value: money(metrics.effective_mrr_usd), sub: '/mo',
                      badge: metrics.mrr_growth_mom_pct ? `${metrics.mrr_growth_mom_pct >= 0 ? '+' : ''}${metrics.mrr_growth_mom_pct.toFixed(1)}% MoM` : undefined,
                      badgeGood: metrics.mrr_growth_mom_pct >= 0 },
                    { label: 'ARR', value: money(metrics.arr_usd) },
                    { label: 'ARPA', value: money(metrics.arpa_usd), sub: '/acct' },
                    { label: 'Paying accounts', value: String(metrics.paying_tenants ?? 0) },
                    { label: 'Active trials', value: String(metrics.active_trials ?? 0), sub: `${metrics.trial_tenants ?? 0} tenants` },
                    { label: 'Logo churn', value: `${(metrics.churn_rate_monthly_pct ?? 0).toFixed(1)}%`, sub: '/mo', warn: (metrics.churn_rate_monthly_pct ?? 0) >= 5 },
                    { label: 'Net new', value: `${(metrics.net_new_tenants ?? 0) >= 0 ? '+' : ''}${metrics.net_new_tenants ?? 0}`, sub: 'this mo',
                      badge: `${metrics.new_tenants_this_month ?? 0} new · ${metrics.churned_this_month ?? 0} churned`, badgeGood: (metrics.net_new_tenants ?? 0) >= 0 },
                    { label: 'MRR at risk', value: money(metrics.mrr_at_risk_usd), sub: `${metrics.past_due_tenants ?? 0} past-due`, warn: (metrics.mrr_at_risk_usd ?? 0) > 0 },
                    { label: 'Est. LTV', value: money(metrics.ltv_estimate_usd), sub: '/acct' },
                  ]
                  return cards.map(c => (
                    <div key={c.label} style={{
                      background: 'var(--color-background-secondary)',
                      border: `0.5px solid ${c.warn ? 'var(--color-border-warning)' : 'var(--color-border-tertiary)'}`,
                      borderRadius: 10, padding: '12px 14px',
                    }}>
                      <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 4 }}>{c.label}</div>
                      <div style={{ fontSize: 22, fontWeight: 600, color: c.warn ? 'var(--color-text-warning)' : 'var(--color-text-primary)' }}>
                        {c.value}{c.sub && <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--color-text-tertiary)', marginLeft: 3 }}>{c.sub}</span>}
                      </div>
                      {c.badge && <div style={{ fontSize: 11, marginTop: 3, color: c.badgeGood ? '#3D9942' : '#C8553D' }}>{c.badge}</div>}
                    </div>
                  ))
                })()}
              </div>
            </div>

            {/* Tier breakdown */}
            <div style={{
              background: 'var(--color-background-primary)',
              border: '0.5px solid var(--color-border-tertiary)',
              borderRadius: 12, padding: '20px 24px', marginBottom: 28,
            }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 16 }}>Plan distribution</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                {[
                  { label: 'Seedling', count: metrics.seedling_tenants, color: '#888780' },
                  { label: 'Grower', count: metrics.grower_tenants, color: '#378ADD' },
                  { label: 'Estate', count: metrics.estate_tenants, color: '#639922' },
                ].map(({ label, count, color }) => {
                  const pct = metrics.total_tenants > 0 ? (count / metrics.total_tenants) * 100 : 0
                  return (
                    <div key={label}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontSize: 12, color }}>{label}</span>
                        <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{count}</span>
                      </div>
                      <div style={{ height: 6, background: 'var(--color-background-secondary)', borderRadius: 3 }}>
                        <div style={{ height: 6, width: `${pct}%`, background: color, borderRadius: 3 }} />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Pending applications shortcut */}
            {applications.length > 0 && (
              <div style={{
                background: '#FAEEDA', border: '0.5px solid #854F0B',
                borderRadius: 12, padding: '14px 18px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span style={{ fontSize: 13, color: '#633806' }}>
                  {applications.length} pending application{applications.length !== 1 ? 's' : ''} awaiting review
                </span>
                <button
                  onClick={() => setTab('applications')}
                  style={{
                    background: '#854F0B', color: '#FAEEDA', border: 'none',
                    borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer',
                  }}
                >
                  Review now
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── APPLICATIONS ──────────────────────────────────────────────────── */}
        {tab === 'applications' && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 500, margin: '0 0 20px' }}>
              Tenant applications
              {applications.length > 0 && (
                <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--color-text-secondary)', marginLeft: 8 }}>
                  {applications.length} pending
                </span>
              )}
            </h2>
            {applications.length === 0 ? (
              <div style={{
                background: 'var(--color-background-primary)',
                border: '0.5px solid var(--color-border-tertiary)',
                borderRadius: 12, padding: 40, textAlign: 'center',
                color: 'var(--color-text-tertiary)', fontSize: 13,
              }}>
                No pending applications
              </div>
            ) : applications.map(app => (
              <div
                key={app.id}
                style={{
                  background: 'var(--color-background-primary)',
                  border: '0.5px solid var(--color-border-tertiary)',
                  borderRadius: 12, padding: '18px 20px', marginBottom: 10,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 500 }}>{app.farm_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                      {app.owner_name} · {app.owner_email}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
                      {app.farm_location} · {app.primary_growable} ·{' '}
                      Requested: <span style={{ color: planColor(app.requested_plan), fontWeight: 500 }}>{app.requested_plan}</span>
                    </div>
                    {app.notes && (
                      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 6, maxWidth: 500 }}>
                        "{app.notes}"
                      </div>
                    )}
                    {app.use_cases?.length > 0 && (
                      <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {app.use_cases.map(u => (
                          <span key={u} style={{
                            fontSize: 11, padding: '2px 8px', borderRadius: 99,
                            background: 'var(--color-background-secondary)',
                            color: 'var(--color-text-secondary)',
                          }}>{u}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                    {new Date(app.submitted_at).toLocaleDateString()}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                  <button
                    onClick={() => { setSelectedApp(app); setApproveForm({ slug: app.farm_name.toLowerCase().replace(/[^a-z0-9]/g, ''), plan: app.requested_plan, note: '' }); setApproveModal(true) }}
                    style={{
                      background: '#EAF3DE', color: '#27500A',
                      border: '0.5px solid #639922', borderRadius: 8,
                      padding: '6px 14px', fontSize: 12, cursor: 'pointer',
                    }}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => { const note = prompt('Rejection reason (sent to applicant):') || ''; if (note !== null) handleReject(app, note) }}
                    style={{
                      background: 'var(--color-background-secondary)',
                      color: 'var(--color-text-secondary)',
                      border: '0.5px solid var(--color-border-secondary)',
                      borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer',
                    }}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── BETA WAITLIST ─────────────────────────────────────────────────── */}
        {tab === 'beta' && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 500, margin: '0 0 6px' }}>
              Beta waitlist
              {betaSignups.length > 0 && (
                <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--color-text-secondary)', marginLeft: 8 }}>
                  {betaSignups.length} pending
                </span>
              )}
            </h2>
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 18 }}>
              Approving creates the tenant on a free 90-day beta trial and emails the grower a setup link → onboarding.
            </div>
            {betaSignups.length === 0 ? (
              <div style={{
                background: 'var(--color-background-primary)', border: '0.5px solid var(--color-border-tertiary)',
                borderRadius: 12, padding: 40, textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 13,
              }}>
                No pending beta signups
              </div>
            ) : betaSignups.map(row => (
              <div key={row.id} style={{
                background: 'var(--color-background-primary)', border: '0.5px solid var(--color-border-tertiary)',
                borderRadius: 12, padding: '16px 20px', marginBottom: 10,
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 500 }}>{row.farm_name || '(no farm name)'}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                    {row.name ? `${row.name} · ` : ''}{row.email}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
                    {[row.postal_code, row.country].filter(Boolean).join(' · ') || 'no location'} · {new Date(row.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button
                    onClick={() => { setSelectedBeta(row); setBetaApproveForm({ slug: betaSlug(row.farm_name || row.name || ''), note: '' }); setBetaApproveModal(true) }}
                    style={{ background: '#EAF3DE', color: '#27500A', border: '0.5px solid #639922', borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer' }}
                  >Approve</button>
                  <button
                    onClick={() => handleBetaReject(row)}
                    style={{ background: 'transparent', color: 'var(--color-text-secondary)', border: '0.5px solid var(--color-border-tertiary)', borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer' }}
                  >Reject</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── TENANTS ───────────────────────────────────────────────────────── */}
        {tab === 'tenants' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <h2 style={{ fontSize: 20, fontWeight: 500, margin: 0 }}>All tenants</h2>
              <button
                onClick={() => setCreateTenantModal(true)}
                style={{
                  background: '#2D6A2F', color: '#fff', border: 'none',
                  borderRadius: 8, padding: '8px 16px', fontSize: 13,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                + Create tenant
              </button>
            </div>
            <div style={{
              background: 'var(--color-background-primary)',
              border: '0.5px solid var(--color-border-tertiary)',
              borderRadius: 12, overflow: 'hidden',
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--color-background-secondary)' }}>
                    {['Farm', 'Slug', 'Plan', 'DB tier', 'Plants', 'Status', 'Billing', 'Actions'].map(h => (
                      <th key={h} style={{
                        padding: '10px 14px', textAlign: 'left', fontWeight: 500,
                        fontSize: 11, color: 'var(--color-text-secondary)',
                        borderBottom: '0.5px solid var(--color-border-tertiary)',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tenants.map((t, i) => (
                    <tr
                      key={t.id}
                      onClick={() => openTenantDrawer(t.id)}
                      style={{
                        borderTop: i > 0 ? '0.5px solid var(--color-border-tertiary)' : 'none',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-background-secondary)')}
                      onMouseLeave={e => (e.currentTarget.style.background = '')}
                    >
                      <td style={{ padding: '10px 14px' }}>
                        <div style={{ fontWeight: 500 }}>{t.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{t.owner_email}</div>
                      </td>
                      <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12 }}>{t.slug}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ color: planColor(t.plan), fontWeight: 500 }}>{t.plan}</span>
                      </td>
                      <td style={{ padding: '10px 14px', color: 'var(--color-text-secondary)' }}>{t.db_tier}</td>
                      <td style={{ padding: '10px 14px', color: 'var(--color-text-secondary)' }}>{t.plant_count}</td>
                      <td style={{ padding: '10px 14px' }}>{statusBadge(t.status)}</td>
                      <td style={{ padding: '10px 14px' }}>{statusBadge(t.billing_status)}</td>
                      <td style={{ padding: '10px 14px' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <select
                            value={t.plan}
                            onChange={e => handleChangeTier(t.id, e.target.value)}
                            style={{
                              fontSize: 11, border: '0.5px solid var(--color-border-secondary)',
                              borderRadius: 6, padding: '3px 6px', background: 'var(--color-background-primary)',
                              color: 'var(--color-text-primary)', cursor: 'pointer',
                            }}
                          >
                            <option value="seedling">seedling</option>
                            <option value="grower">grower</option>
                            <option value="estate">estate</option>
                          </select>
                          {t.status !== 'suspended' && (
                            <button
                              onClick={() => handleSuspend(t.id)}
                              style={{
                                fontSize: 11, border: '0.5px solid var(--color-border-secondary)',
                                borderRadius: 6, padding: '3px 8px', cursor: 'pointer',
                                background: 'var(--color-background-secondary)',
                                color: 'var(--color-text-secondary)',
                              }}
                            >
                              Suspend
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteTenant(t.id, t.name)}
                            style={{
                              fontSize: 11, border: '0.5px solid #F87171',
                              borderRadius: 6, padding: '3px 8px', cursor: 'pointer',
                              background: '#FEF2F2', color: '#B91C1C',
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── AUDIT LOGS ───────────────────────────────────────────────────── */}
        {tab === 'audit' && (
          <AdminAuditPanel tenants={tenants} />
        )}

        {/* ── GROWABLE REF ──────────────────────────────────────────────────── */}
        {tab === 'growable' && <GrowableRefPanel showToast={showToast} />}

        {/* ── RELEASES ──────────────────────────────────────────────────────── */}
        {tab === 'releases' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div>
                <h2 style={{ fontSize: 20, fontWeight: 500, margin: '0 0 4px' }}>Platform releases</h2>
                <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: 0 }}>
                  Publish release notes — users see a summary modal on next login.
                </p>
              </div>
              <button
                onClick={() => { setReleaseForm({ ...BLANK_RELEASE }); setReleaseModal(true) }}
                style={{
                  background: '#2D6A2F', color: '#fff', border: 'none',
                  borderRadius: 8, padding: '8px 16px', fontSize: 13,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                + New release
              </button>
            </div>

            {releases.length === 0 ? (
              <div style={{
                background: 'var(--color-background-primary)',
                border: '0.5px solid var(--color-border-tertiary)',
                borderRadius: 12, padding: 40, textAlign: 'center',
                color: 'var(--color-text-tertiary)', fontSize: 13,
              }}>
                No releases yet. Create your first release note above.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {releases.map((r) => (
                  <div
                    key={r.id}
                    style={{
                      background: 'var(--color-background-primary)',
                      border: '0.5px solid var(--color-border-tertiary)',
                      borderRadius: 12, padding: '18px 20px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                          <span style={{
                            fontSize: 11, fontWeight: 700, fontFamily: 'monospace',
                            background: 'var(--color-background-secondary)',
                            color: 'var(--color-text-secondary)',
                            padding: '2px 8px', borderRadius: 6,
                          }}>v{r.version}</span>
                          <span style={{
                            fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 99,
                            background: r.is_published ? '#EAF3DE' : '#F1EFE8',
                            color: r.is_published ? '#27500A' : '#888780',
                          }}>
                            {r.is_published ? '● Published' : '○ Draft'}
                          </span>
                          {r.published_at && (
                            <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                              {new Date(r.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-text-primary)', marginBottom: 6 }}>{r.title}</div>
                        {r.summary_bullets?.filter(Boolean).length > 0 && (
                          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {r.summary_bullets.filter(Boolean).map((b: string, i: number) => (
                              <li key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                <span style={{ fontSize: 10, color: '#F5A623', marginTop: 3 }}>✦</span>
                                <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>{b}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                        <button
                          onClick={async () => {
                            const updated = await adminApi.put(`/releases/${r.id}`, { is_published: !r.is_published })
                            setReleases(rs => rs.map(x => x.id === r.id ? { ...x, ...updated } : x))
                            showToast(r.is_published ? 'Release unpublished' : 'Release published')
                          }}
                          style={{
                            fontSize: 11, padding: '5px 12px', borderRadius: 7, cursor: 'pointer',
                            border: '0.5px solid var(--color-border-secondary)',
                            background: 'var(--color-background-secondary)',
                            color: 'var(--color-text-secondary)',
                          }}
                        >
                          {r.is_published ? 'Unpublish' : 'Publish'}
                        </button>
                        <button
                          onClick={() => {
                            setReleaseForm({
                              version: r.version,
                              title: r.title,
                              body_markdown: r.body_markdown ?? '',
                              summary_bullets: [...(r.summary_bullets ?? []), '', '', ''].slice(0, 3),
                              is_published: r.is_published,
                            })
                            setReleaseModal(true)
                          }}
                          style={{
                            fontSize: 11, padding: '5px 12px', borderRadius: 7, cursor: 'pointer',
                            border: '0.5px solid var(--color-border-secondary)',
                            background: 'var(--color-background-secondary)',
                            color: 'var(--color-text-secondary)',
                          }}
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'docs' && (
          <div style={{ margin: '-24px -24px 0', background: 'var(--color-background-primary)' }}>
            <DocsPage embedded />
          </div>
        )}

        {tab === 'support' && <SupportQueuePanel />}
        {tab === 'ai_quotas' && <QuotaDefaultsPanel showToast={showToast} />}
        {tab === 'email' && <EmailTemplatesPanel showToast={showToast} />}

        {tab === 'maps' && <ParcelsMapPanel />}
      </div>

      {/* ── RELEASE MODAL ────────────────────────────────────────────────────── */}
      {releaseModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
        }}>
          <div style={{
            background: 'var(--color-background-primary)',
            borderRadius: 16, padding: 28, width: 520,
            border: '0.5px solid var(--color-border-secondary)',
            maxHeight: '85vh', overflowY: 'auto',
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 500, margin: '0 0 4px' }}>
              {releaseForm.version ? `Edit release v${releaseForm.version}` : 'New release'}
            </h3>
            <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', margin: '0 0 20px' }}>
              Summary bullets appear in the "What's new" popup users see on next login.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>
                  Version <span style={{ color: '#F87171' }}>*</span>
                </label>
                <input
                  value={releaseForm.version}
                  onChange={e => setReleaseForm(f => ({ ...f, version: e.target.value }))}
                  placeholder="1.4.2"
                  style={{ ...ADMIN_FIELD, fontFamily: 'monospace' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>
                  Title <span style={{ color: '#F87171' }}>*</span>
                </label>
                <input
                  value={releaseForm.title}
                  onChange={e => setReleaseForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Irrigation improvements & map updates"
                  style={{ ...ADMIN_FIELD }}
                />
              </div>
            </div>

            <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>
              Summary bullets (shown in popup — up to 3)
            </label>
            {[0, 1, 2].map(i => (
              <input
                key={i}
                value={releaseForm.summary_bullets[i] ?? ''}
                onChange={e => setReleaseForm(f => {
                  const bullets = [...f.summary_bullets]
                  bullets[i] = e.target.value
                  return { ...f, summary_bullets: bullets }
                })}
                placeholder={`Bullet ${i + 1} — short feature summary`}
                style={{ ...ADMIN_FIELD, marginBottom: 8 }}
              />
            ))}

            <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, marginTop: 6 }}>
              Full release notes (Markdown — internal reference)
            </label>
            <textarea
              value={releaseForm.body_markdown}
              onChange={e => setReleaseForm(f => ({ ...f, body_markdown: e.target.value }))}
              placeholder="## What changed&#10;&#10;- Fixed irrigation zone persistence&#10;- Added debounced map search"
              rows={6}
              style={{ ...ADMIN_FIELD, marginBottom: 16, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
              <input
                type="checkbox"
                id="publish-now"
                checked={releaseForm.is_published}
                onChange={e => setReleaseForm(f => ({ ...f, is_published: e.target.checked }))}
              />
              <label htmlFor="publish-now" style={{ fontSize: 12, color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
                Publish immediately (users will see popup on next login)
              </label>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setReleaseModal(false); setReleaseForm({ ...BLANK_RELEASE }) }}
                style={{
                  background: 'none', border: '0.5px solid var(--color-border-secondary)',
                  borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer',
                  color: 'var(--color-text-secondary)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!releaseForm.version || !releaseForm.title) return
                  setLoading(true)
                  try {
                    const payload = {
                      ...releaseForm,
                      summary_bullets: releaseForm.summary_bullets.filter(Boolean),
                    }
                    // Check if editing existing release
                    const existing = releases.find(r => r.version === releaseForm.version)
                    if (existing) {
                      const updated = await adminApi.put(`/releases/${existing.id}`, payload)
                      setReleases(rs => rs.map(r => r.id === existing.id ? { ...r, ...updated } : r))
                      showToast(`Release v${releaseForm.version} updated`)
                    } else {
                      const created = await adminApi.post('/releases', payload)
                      setReleases(rs => [created, ...rs])
                      showToast(`Release v${releaseForm.version} created`)
                    }
                    setReleaseModal(false)
                    setReleaseForm({ ...BLANK_RELEASE })
                  } catch (e) {
                    showToast(apiError(e, 'Save failed'), 'error')
                  }
                  setLoading(false)
                }}
                disabled={loading || !releaseForm.version || !releaseForm.title}
                style={{
                  background: '#2D6A2F', color: '#fff', border: 'none',
                  borderRadius: 8, padding: '8px 20px', fontSize: 13,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading || !releaseForm.version || !releaseForm.title ? 0.6 : 1,
                }}
              >
                {loading ? 'Saving…' : 'Save release'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── APPROVE MODAL ────────────────────────────────────────────────────── */}
      {approveModal && selectedApp && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
        }}>
          <div style={{
            background: 'var(--color-background-primary)',
            borderRadius: 16, padding: 28, width: 440,
            border: '0.5px solid var(--color-border-secondary)',
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 500, margin: '0 0 4px' }}>Approve {selectedApp.farm_name}</h3>
            <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', margin: '0 0 20px' }}>
              This will create the tenant account and send an invite email to {selectedApp.owner_email}.
            </p>

            <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Tenant slug (subdomain)</label>
            <input
              value={approveForm.slug}
              onChange={e => setApproveForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
              placeholder="zillfarm"
              style={{ ...ADMIN_FIELD, marginBottom: 14 }}
            />

            <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Plan</label>
            <select
              value={approveForm.plan}
              onChange={e => setApproveForm(f => ({ ...f, plan: e.target.value }))}
              style={{ ...ADMIN_SELECT, marginBottom: 14 }}
            >
              <option value="seedling">Seedling — free, 25 plants</option>
              <option value="grower">Grower — $99/yr, 250 plants, analytics</option>
              <option value="estate">Estate — $199/yr, unlimited, dedicated DB</option>
            </select>

            <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Note to tenant (optional)</label>
            <textarea
              value={approveForm.note}
              onChange={e => setApproveForm(f => ({ ...f, note: e.target.value }))}
              placeholder="Welcome to Grafty! Your farm has been approved..."
              rows={3}
              style={{ ...ADMIN_FIELD, marginBottom: 20, resize: 'vertical' }}
            />

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setApproveModal(false); setSelectedApp(null) }}
                style={{
                  background: 'none', border: '0.5px solid var(--color-border-secondary)',
                  borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer',
                  color: 'var(--color-text-secondary)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={loading || !approveForm.slug}
                style={{
                  background: '#2D6A2F', color: '#fff', border: 'none',
                  borderRadius: 8, padding: '8px 20px', fontSize: 13,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading || !approveForm.slug ? 0.6 : 1,
                }}
              >
                {loading ? 'Approving...' : 'Approve and send invite'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── BETA APPROVE MODAL ───────────────────────────────────────────────── */}
      {betaApproveModal && selectedBeta && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
        }}>
          <div style={{
            background: 'var(--color-background-primary)', borderRadius: 16, padding: 28, width: 440,
            border: '0.5px solid var(--color-border-secondary)',
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 500, margin: '0 0 4px' }}>Approve {selectedBeta.farm_name || selectedBeta.email}</h3>
            <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', margin: '0 0 20px' }}>
              Creates the tenant on a free 90-day beta trial (full features) and emails {selectedBeta.email} a setup link to finish onboarding.
            </p>

            <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Tenant slug (subdomain)</label>
            <input
              value={betaApproveForm.slug}
              onChange={e => setBetaApproveForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
              placeholder="sunny-acres"
              style={{ ...ADMIN_FIELD, marginBottom: 6 }}
            />
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 14 }}>
              Leave blank to auto-generate from the farm name. → {betaApproveForm.slug || betaSlug(selectedBeta.farm_name || selectedBeta.name || 'farm')}.grafty.io
            </div>

            <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Note (optional, internal)</label>
            <textarea
              value={betaApproveForm.note}
              onChange={e => setBetaApproveForm(f => ({ ...f, note: e.target.value }))}
              rows={2}
              style={{ ...ADMIN_FIELD, marginBottom: 20, resize: 'vertical' }}
            />

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setBetaApproveModal(false); setSelectedBeta(null) }}
                style={{ background: 'none', border: '0.5px solid var(--color-border-secondary)', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer', color: 'var(--color-text-secondary)' }}
              >Cancel</button>
              <button
                onClick={handleBetaApprove}
                disabled={loading}
                style={{ background: '#2D6A2F', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}
              >{loading ? 'Approving…' : 'Approve & invite'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── CREATE TENANT MODAL ──────────────────────────────────────────────── */}
      {createTenantModal && (() => {
        // Shared dark-theme styles for all inputs and selects inside this modal.
        // The admin portal renders in the dark theme context but bare <input>/<select>
        // elements fall back to browser-default white backgrounds without explicit styles.
        const inp: React.CSSProperties = {
          width: '100%', boxSizing: 'border-box',
          background: 'var(--color-background-tertiary)',
          border: '0.5px solid var(--color-border-secondary)',
          borderRadius: 8, color: 'var(--color-text-primary)',
          fontSize: 13, padding: '8px 10px', outline: 'none',
        }
        const sel: React.CSSProperties = { ...inp, cursor: 'pointer' }
        return (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
        }}>
          <div style={{
            background: 'var(--color-background-primary)',
            borderRadius: 16, padding: 28, width: 480,
            border: '0.5px solid var(--color-border-secondary)',
            boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 4px', color: 'var(--color-text-primary)' }}>Create tenant</h3>
            <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', margin: '0 0 20px', lineHeight: 1.5 }}>
              Directly provisions a tenant without going through the application queue. The owner will receive an invite email.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Farm name <span style={{ color: '#F87171' }}>*</span>
                </label>
                <input
                  value={createTenantForm.name}
                  onChange={e => setCreateTenantForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Zill Farm"
                  style={inp}
                />
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Slug (subdomain) <span style={{ color: '#F87171' }}>*</span>
                </label>
                <input
                  value={createTenantForm.slug}
                  onChange={e => setCreateTenantForm(f => ({
                    ...f,
                    slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                  }))}
                  placeholder="zillfarm"
                  style={{ ...inp, fontFamily: 'monospace' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Owner name
                </label>
                <input
                  value={createTenantForm.owner_name}
                  onChange={e => setCreateTenantForm(f => ({ ...f, owner_name: e.target.value }))}
                  placeholder="Gary Zill"
                  style={inp}
                />
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Owner email <span style={{ color: '#F87171' }}>*</span>
                </label>
                <input
                  type="email"
                  value={createTenantForm.owner_email}
                  onChange={e => setCreateTenantForm(f => ({ ...f, owner_email: e.target.value }))}
                  placeholder="gary@zillfarm.com"
                  style={inp}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              <div>
                <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Plan</label>
                <select
                  value={createTenantForm.plan}
                  onChange={e => setCreateTenantForm(f => ({ ...f, plan: e.target.value }))}
                  style={sel}
                >
                  <option value="seedling">Seedling — free, 25 plants</option>
                  <option value="grower">Grower — $99/yr, 250 plants</option>
                  <option value="estate">Estate — $199/yr, unlimited</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Farm type</label>
                <select
                  value={createTenantForm.farm_type}
                  onChange={e => setCreateTenantForm(f => ({ ...f, farm_type: e.target.value }))}
                  style={sel}
                >
                  <option value="mango_orchard">Mango orchard</option>
                  <option value="peach_orchard">Peach orchard</option>
                  <option value="apple_orchard">Apple orchard</option>
                  <option value="citrus">Citrus grove</option>
                  <option value="avocado">Avocado grove</option>
                  <option value="lychee">Lychee / longan</option>
                  <option value="palm">Palm grove</option>
                  <option value="mixed_fruit">Mixed fruit orchard</option>
                  <option value="food_forest">Food forest</option>
                  <option value="nursery">Nursery / propagation</option>
                  <option value="vineyard">Vineyard</option>
                  <option value="hobby">Hobby farm</option>
                  <option value="general">General / other</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setCreateTenantModal(false); setCreateTenantForm({ ...BLANK_CREATE_TENANT }) }}
                style={{
                  background: 'none', border: '0.5px solid var(--color-border-secondary)',
                  borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer',
                  color: 'var(--color-text-secondary)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTenant}
                disabled={loading || !createTenantForm.slug || !createTenantForm.name || !createTenantForm.owner_email}
                style={{
                  background: '#2D6A2F', color: '#fff', border: 'none',
                  borderRadius: 8, padding: '8px 20px', fontSize: 13,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading || !createTenantForm.slug || !createTenantForm.name || !createTenantForm.owner_email ? 0.6 : 1,
                }}
              >
                {loading ? 'Creating…' : 'Create tenant'}
              </button>
            </div>
          </div>
        </div>
        )
      })()}

      {/* Tenant detail drawer */}
      {(tenantDrawer || drawerLoading) && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setTenantDrawer(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200 }}
          />
          {/* Drawer */}
          <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0, width: 540,
            background: 'var(--color-background-primary)',
            borderLeft: '0.5px solid var(--color-border-tertiary)',
            zIndex: 201, overflowY: 'auto', padding: '24px 28px',
          }}>
            {drawerLoading ? (
              <div style={{ color: 'var(--color-text-tertiary)', fontSize: 13, marginTop: 60, textAlign: 'center' }}>
                Loading…
              </div>
            ) : tenantDrawer && (() => {
              const d = tenantDrawer
              const maxCount = Math.max(...d.feature_usage.map(f => f.event_count), 1)
              const roleColor = (role: string) => ({ owner: '#F5A623', admin: '#60A5FA', superuser: '#A78BFA' }[role] ?? 'var(--color-text-secondary)')
              const statusDot = (status: string) => status === 'active' ? '#4CAF50' : status === 'invited' ? '#F5A623' : '#F87171'

              return (
                <>
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 600 }}>{d.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                        <span style={{ fontFamily: 'monospace' }}>{d.slug}</span>
                        {' · '}
                        <span style={{ color: '#F5A623', fontWeight: 500 }}>{d.plan}</span>
                        {' · '}
                        <span>{d.status}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => setTenantDrawer(null)}
                      style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--color-text-secondary)', lineHeight: 1 }}
                    >×</button>
                  </div>

                  {/* Metric cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 24 }}>
                    {[
                      { label: 'Members', value: d.member_count },
                      { label: 'Plants', value: d.plant_count },
                      { label: 'Harvests', value: d.harvest_count },
                      { label: 'Events (30d)', value: d.audit_events_30d },
                    ].map(({ label, value }) => (
                      <div key={label} style={{
                        background: 'var(--color-background-secondary)',
                        borderRadius: 10, padding: '12px 14px',
                        border: '0.5px solid var(--color-border-tertiary)',
                      }}>
                        <div style={{ fontSize: 20, fontWeight: 600 }}>{value}</div>
                        <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Feature usage */}
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                      Feature usage — last 30 days
                    </div>
                    {d.feature_usage.length === 0 ? (
                      <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', padding: '12px 0' }}>No activity recorded yet</div>
                    ) : d.feature_usage.map(f => (
                      <div key={f.feature} style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                          <span style={{ textTransform: 'capitalize' }}>{f.feature.replace(/_/g, ' ')}</span>
                          <span style={{ color: 'var(--color-text-tertiary)' }}>{f.event_count} events</span>
                        </div>
                        <div style={{ height: 6, borderRadius: 3, background: 'var(--color-background-secondary)', overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', borderRadius: 3, background: '#2D6A2F',
                            width: `${Math.round((f.event_count / maxCount) * 100)}%`,
                          }} />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Storefront payments — Stripe Connect charge mode */}
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                      Storefront payments
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <label style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Charge mode</label>
                      <select
                        value={d.storefront_charge_mode || 'direct'}
                        onChange={e => handleChargeMode(d.id, e.target.value)}
                        style={{
                          fontSize: 12, border: '0.5px solid var(--color-border-secondary)',
                          borderRadius: 6, padding: '4px 8px', background: 'var(--color-background-primary)',
                          color: 'var(--color-text-primary)', cursor: 'pointer',
                        }}
                      >
                        <option value="direct">Direct — farm is merchant of record (default)</option>
                        <option value="destination">Destination — Grafty is merchant of record</option>
                      </select>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 6, lineHeight: 1.5 }}>
                      Direct: the charge is created on the farm's connected Stripe account — the farm
                      bears its own chargebacks &amp; Stripe fees; Grafty takes the application fee.
                      Destination: the charge runs on Grafty's account and is transferred to the farm.
                    </div>
                  </div>

                  {/* Tree detection engine — Gemini vs DeepForest (per tenant) */}
                  <DetectionEngineToggle tenantId={d.id} showToast={showToast} />

                  {/* AI quotas — per-tenant overrides */}
                  <div style={{ marginBottom: 24 }}>
                    <TenantQuotaPanel tenantId={d.id} showToast={showToast} />
                  </div>

                  {/* Members */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                      Members ({d.member_count})
                    </div>
                    {d.members.length === 0 ? (
                      <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>No members</div>
                    ) : d.members.map(m => (
                      <div key={m.id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '9px 0',
                        borderBottom: '0.5px solid var(--color-border-tertiary)',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 30, height: 30, borderRadius: '50%',
                            background: 'var(--color-background-secondary)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 12, fontWeight: 600, color: roleColor(m.role),
                            flexShrink: 0,
                          }}>
                            {(m.display_name || m.email)[0].toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 500 }}>{m.display_name || m.email}</div>
                            {m.display_name && <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{m.email}</div>}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                            <span style={{
                              fontSize: 11, fontWeight: 500, padding: '2px 7px', borderRadius: 99,
                              background: 'var(--color-background-secondary)',
                              color: roleColor(m.role),
                            }}>{m.role}</span>
                            <span style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 3, color: 'var(--color-text-tertiary)' }}>
                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusDot(m.status), display: 'inline-block' }} />
                              {m.status}
                            </span>
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
                            {m.last_login_at
                              ? `Last login ${new Date(m.last_login_at).toLocaleDateString()}`
                              : 'Never logged in'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )
            })()}
          </div>
        </>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24,
          background: toast.type === 'error' ? '#A32D2D' : '#27500A',
          color: '#fff', borderRadius: 10, padding: '12px 18px', fontSize: 13,
          zIndex: 100,
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  )
}

// ── Support queue (staff, cross-tenant — §197) ───────────────────────────────
const SQ = {
  card: 'var(--color-bg-card)', bg: 'var(--color-bg)', text: 'var(--color-text-primary)',
  muted: 'var(--color-text-secondary)', border: 'var(--color-border)',
}
const SQ_STATUS: Record<string, string> = {
  open: '#F5A623', in_progress: '#3B82F6', waiting_on_customer: '#A06CD5', resolved: '#2D9B5A', closed: '#8A8F87',
}
function SupportQueuePanel() {
  const [tickets, setTickets] = useState<SupportTicket[]>([])
  const [sel, setSel] = useState<SupportTicket | null>(null)
  const [reply, setReply] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [kind, setKind] = useState('') // '' all · 'feedback' · 'support'

  const load = () => { supportApi.staffList(kind).then(r => setTickets(r.data.tickets ?? [])).catch(e => setErr(apiError(e, 'load failed'))) }
  useEffect(load, [kind])
  const open = (t: SupportTicket) => { setReply(''); supportApi.staffGet(t.tenant_id, t.id).then(r => setSel(r.data)).catch(e => setErr(apiError(e, 'load failed'))) }
  const send = async () => {
    if (!sel || !reply.trim()) return
    setBusy(true); setErr('')
    try { await supportApi.staffReply(sel.tenant_id, sel.id, reply); setReply(''); const r = await supportApi.staffGet(sel.tenant_id, sel.id); setSel(r.data); load() }
    catch (e) { setErr(apiError(e, 'reply failed')) } finally { setBusy(false) }
  }
  const setStatus = async (status: 'resolved' | 'closed') => {
    if (!sel) return
    setBusy(true)
    try { await supportApi.staffSetStatus(sel.tenant_id, sel.id, status); const r = await supportApi.staffGet(sel.tenant_id, sel.id); setSel(r.data); load() }
    catch (e) { setErr(apiError(e, 'update failed')) } finally { setBusy(false) }
  }
  const fmt = (s: string) => new Date(s).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })

  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: SQ.text, margin: '0 0 4px' }}>Support queue</h2>
      <p style={{ color: SQ.muted, fontSize: 13, marginTop: 0 }}>Open tickets + product feedback across all tenants. Reply as Grafty staff.</p>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {[{ k: '', l: 'All' }, { k: 'feedback', l: 'Feedback' }, { k: 'support', l: 'Support' }].map(o => (
          <button key={o.k} onClick={() => setKind(o.k)} style={{
            padding: '5px 12px', borderRadius: 999, fontSize: 12, cursor: 'pointer',
            border: `1px solid ${kind === o.k ? SQ.text : SQ.border}`,
            background: kind === o.k ? SQ.bg : 'transparent', color: SQ.text,
          }}>{o.l}</button>
        ))}
      </div>
      {err && <p style={{ color: '#E5484D', fontSize: 12 }}>{err}</p>}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 320px', minWidth: 280, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {tickets.length === 0 && <p style={{ color: SQ.muted, fontSize: 13 }}>No open tickets. 🎉</p>}
          {tickets.map(t => (
            <button key={t.id} onClick={() => open(t)} style={{ textAlign: 'left', background: sel?.id === t.id ? SQ.bg : SQ.card, border: `1px solid ${sel?.id === t.id ? SQ_STATUS[t.status] : SQ.border}`, borderRadius: 10, padding: '10px 12px', cursor: 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ color: SQ.text, fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</span>
                <span style={{ color: SQ_STATUS[t.status], fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{t.status.replace(/_/g, ' ')}</span>
              </div>
              <div style={{ color: SQ.muted, fontSize: 12, marginTop: 2 }}>
                {t.kind === 'feedback' && <span style={{ color: '#C77DFF', fontWeight: 700 }}>Feedback · </span>}
                {t.tenant_name || t.tenant_id.slice(0, 8)} · {t.priority}{t.rating ? ` · ${'★'.repeat(t.rating)}` : ''} · {fmt(t.updated_at)}
              </div>
            </button>
          ))}
        </div>
        {sel && (
          <div style={{ flex: '2 1 380px', minWidth: 320, background: SQ.card, border: `1px solid ${SQ.border}`, borderRadius: 12, padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <strong style={{ color: SQ.text, fontSize: 15 }}>{sel.subject}</strong>
              <span style={{ color: SQ_STATUS[sel.status], fontSize: 12, fontWeight: 700 }}>{sel.status.replace(/_/g, ' ')}</span>
            </div>
            <div style={{ color: SQ.muted, fontSize: 12, margin: '2px 0 12px' }}>
              {sel.kind === 'feedback' ? 'Feedback · ' : ''}{sel.tenant_name} · {sel.category} · {sel.priority}{sel.rating ? ` · ${'★'.repeat(sel.rating)}` : ''}
              {sel.page_url ? <span style={{ display: 'block', marginTop: 2, color: SQ.muted }}>from {sel.page_url}</span> : null}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '40vh', overflowY: 'auto' }}>
              <div style={{ background: SQ.bg, border: `1px solid ${SQ.border}`, borderRadius: 8, padding: '8px 10px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: SQ.text, marginBottom: 3 }}>Tenant · {fmt(sel.created_at)}</div>
                <div style={{ color: SQ.text, fontSize: 14, whiteSpace: 'pre-wrap' }}>{sel.body}</div>
              </div>
              {(sel.replies ?? []).map(r => (
                <div key={r.id} style={{ background: r.is_staff ? 'rgba(45,155,90,0.10)' : SQ.bg, border: `1px solid ${r.is_staff ? 'rgba(45,155,90,0.35)' : SQ.border}`, borderRadius: 8, padding: '8px 10px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: r.is_staff ? '#2D9B5A' : SQ.text, marginBottom: 3 }}>{r.author_name}{r.is_staff ? ' · Grafty' : ''} · {fmt(r.created_at)}</div>
                  <div style={{ color: SQ.text, fontSize: 14, whiteSpace: 'pre-wrap' }}>{r.body}</div>
                </div>
              ))}
            </div>
            <textarea value={reply} onChange={e => setReply(e.target.value)} rows={3} placeholder="Reply as Grafty staff…" style={{ width: '100%', marginTop: 10, background: SQ.bg, border: `1px solid ${SQ.border}`, borderRadius: 8, padding: '8px 10px', color: SQ.text, fontSize: 14, resize: 'vertical' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
              <span style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setStatus('resolved')} disabled={busy} style={{ background: 'transparent', border: `1px solid ${SQ.border}`, color: SQ.muted, borderRadius: 8, padding: '8px 12px', fontSize: 12, cursor: 'pointer' }}>Mark resolved</button>
                <button onClick={() => setStatus('closed')} disabled={busy} style={{ background: 'transparent', border: `1px solid ${SQ.border}`, color: SQ.muted, borderRadius: 8, padding: '8px 12px', fontSize: 12, cursor: 'pointer' }}>Close</button>
              </span>
              <button onClick={send} disabled={busy || !reply.trim()} style={{ background: 'var(--color-primary)', color: '#1a1207', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{busy ? '…' : 'Reply'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
